import { useState, useCallback, useEffect } from "react";
import Modal from "../../../../../components/Modal/Modal";
import Input from "../../../../../components/Input/Input";
import Dropdown from "../../../../../components/Dropdown/Dropdown";
import Button from "../../../../../components/Button/Button";
import DownloadIcon from "../../../../../assets/svg/download-icon";
import CloseIcon from "../../../../../assets/svg/close-icon";
import { getFileTypeIcon } from "../../../../../utils/functions/file-icon-extractor";
import type { DropdownOption } from "../../../../../components/Dropdown/types";

const FILE_FORMAT_OPTIONS: DropdownOption[] = [
  {
    value: "csv",
    content: (
      <div className="flex items-center gap-2">
        <img src={getFileTypeIcon("file.csv")} alt="CSV" className="w-5 h-5" />
        <span>CSV</span>
      </div>
    ),
    selectedOptionContent: (
      <div className="flex items-center gap-2">
        <img src={getFileTypeIcon("file.csv")} alt="CSV" className="w-4 h-4" />
        <span>CSV</span>
      </div>
    ),
  },
  {
    value: "xlsx",
    content: (
      <div className="flex items-center gap-2">
        <img
          src={getFileTypeIcon("file.xlsx")}
          alt="XLSX"
          className="w-5 h-5"
        />
        <span>XLSX</span>
      </div>
    ),
    selectedOptionContent: (
      <div className="flex items-center gap-2">
        <img
          src={getFileTypeIcon("file.xlsx")}
          alt="XLSX"
          className="w-4 h-4"
        />
        <span>XLSX</span>
      </div>
    ),
  },
];

interface ExportContactsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  totalCount?: number;
  defaultName?: string;
  loading?: boolean;
  onExport: (args: { exportName: string; fileFormat: string }) => void;
}

const ExportContactsModal = ({
  open,
  onOpenChange,
  totalCount = 0,
  defaultName = "All Contacts",
  loading = false,
  onExport,
}: ExportContactsModalProps) => {
  const [exportName, setExportName] = useState(defaultName);
  const [fileFormat, setFileFormat] = useState("csv");

  useEffect(() => {
    if (open) setExportName(defaultName);
  }, [open, defaultName]);

  const handleExport = useCallback(() => {
    onExport({ exportName, fileFormat });
  }, [onExport, exportName, fileFormat]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const customHeader = (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3">
        <DownloadIcon className="w-6 h-6 text-verba-blue dark:text-verba-blue-dk" />
        <h1 className="text-lg font-semibold text-verba-indigo dark:text-verba-indigo-dk">
          Prepare your export file
        </h1>
      </div>
      <button
        className="w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
        onClick={() => onOpenChange(false)}
      >
        <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title=""
      showHeader
      showCloseButton={false}
      customHeader={customHeader}
      headerClassName="px-6 pt-6 pb-0"
      bodyClassName="!p-0 flex flex-col"
      contentClassName="w-[460px] max-w-[460px] bg-pure-white dark:bg-pure-white-dk rounded-[16px]"
      disableDefaultAnimation
      contentStyle={{
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
      }}
    >
      <div className="flex flex-col px-6 pt-5 pb-6 gap-5">
        <p className="text-md text-dark-gray dark:text-dark-gray-dk">
          Exporting:{" "}
          <span className="font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            {defaultName}
          </span>{" "}
          {totalCount.toLocaleString()} rows in 1 file
        </p>

        <div className="flex flex-col gap-1.5">
          <Input
            label="Export name"
            value={exportName}
            onChange={(e) => setExportName(e.target.value)}
            placeholder="Enter export name"
            labelClassName="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk"
            inputClassName="!text-md !rounded-lg border"
            secondaryContainerClassName="!rounded-lg border border-border-gray dark:border-border-gray-dk p-2"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Dropdown
            label="File format"
            labelClassName="text-md font-medium text-verba-field-label dark:text-verba-field-label-dk"
            options={FILE_FORMAT_OPTIONS}
            value={fileFormat}
            onChange={(val) => setFileFormat(val)}
            triggerClassName=" !rounded-lg"
            dropdownClassName="!rounded-lg"
            selectedOptionTextClassName="text-md"
          />
        </div>
        {/* 
        <div className="flex flex-col gap-2">
          <h3 className="text-md font-semibold text-verba-indigo dark:text-verba-indigo-dk">
            Data included in export
          </h3>
          <ul className="flex flex-col gap-1.5 pl-1">
            <li className="flex items-start gap-2 text-md text-verba-indigo dark:text-verba-indigo-dk">
              <span className="mt-[7px] w-[4px] h-[4px] rounded-full bg-verba-indigo dark:bg-verba-indigo-dk shrink-0" />
              Properties in your view
            </li>
            <li className="flex items-start gap-2 text-md text-verba-indigo dark:text-verba-indigo-dk">
              <span className="mt-[7px] w-[4px] h-[4px] rounded-full bg-verba-indigo dark:bg-verba-indigo-dk shrink-0" />
              Associations in your view
            </li>
            <li className="flex items-start gap-2 text-md text-verba-indigo dark:text-verba-indigo-dk">
              <span className="mt-[7px] w-[4px] h-[4px] rounded-full bg-verba-indigo dark:bg-verba-indigo-dk shrink-0" />
              Up to 1,000 associated records in each association column of each
              row
            </li>
            <li className="flex items-start gap-2 text-md text-dark-gray dark:text-dark-gray-dk">
              <span className="mt-[7px] w-[4px] h-[4px] rounded-full bg-verba-indigo dark:bg-verba-indigo-dk shrink-0" />
              <span>
                <span className="font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                  Associated record name
                </span>{" "}
                and{" "}
                <span className="font-semibold text-verba-indigo dark:text-verba-indigo-dk">
                  associated record ID
                </span>
              </span>
            </li>
          </ul>
        </div> */}
      </div>

      <div className="flex items-center justify-end gap-3 px-6 py-4">
        <Button
          type="button"
          innerText="Cancel"
          onClick={handleCancel}
          buttonContainerClass="px-[48px] py-1.5 border border-border-gray dark:border-border-gray-dk bg-pure-white dark:bg-pure-white-dk hover:bg-hover-effect dark:hover:bg-hover-effect-dk rounded-[6px]"
          innerTextClass="text-md font-medium text-heading-text dark:text-heading-text-dk"
        />
        <Button
          type="button"
          innerText={loading ? "Exporting..." : "Export"}
          onClick={handleExport}
          disabled={!exportName.trim() || loading}
          buttonContainerClass="px-[48px] py-1.5 bg-verba-indigo dark:bg-verba-indigo-dk hover:opacity-90 rounded-[6px] disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium text-pure-white dark:text-pure-white-dk"
        />
      </div>
    </Modal>
  );
};

export default ExportContactsModal;
